import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { factory } from "../../../api/apiFactory";
import { showNotification } from "../../../app/slices/notificationSlice";

/** Endpoints return either `data.data[0]` (getById) or `data.data`. */
export const pickDetailDefault = (res) => {
  const d = res?.data?.data;
  if (Array.isArray(d)) return d[0];
  return d ?? res?.data;
};

/** Same expression the newer slices use, with a fallback for network errors. */
export const readError = (err) =>
  err?.response?.data?.errors?.[0] ?? err?.response?.data?.message ?? "حدث خطأ";

/**
 * Saves a single field.
 *
 * It calls the entity's endpoint module directly rather than dispatching the
 * entity's `update` thunk, because every slice's `setDialog` is a *toggle*
 * (`state.x.dialog = !state.x.dialog`) that the thunks fire on success — going
 * through the thunk would pop the create/edit dialog open after every inline
 * save. `setLoading` is a toggle too and desyncs under concurrency, so the
 * saving flag is local state here.
 *
 * The backend PUT is full-replace (there is no PATCH anywhere in this app), so
 * the body is always the entity's whole form payload with one field patched.
 * When `hydrate` is on we GET the record first, because the list row is often a
 * narrower projection than what the form submits.
 */
export const useInlineSave = (config) => {
  const dispatch = useDispatch();
  const [saving, setSaving] = useState(false);
  const alive = useRef(true);

  useEffect(
    () => () => {
      alive.current = false;
    },
    [],
  );

  const repo = config?.api ? factory.get(config.api) : undefined;

  const getId = useCallback(
    (row) =>
      config?.getId ? config.getId(row) : row?.[config?.idField ?? "id"],
    [config],
  );

  const fetchDetail = useCallback(
    async (row) => {
      if (!repo?.getById) return null;
      const pick = config?.pickDetail ?? pickDetailDefault;
      return pick(await repo.getById(getId(row)));
    },
    [repo, config, getId],
  );

  const save = useCallback(
    async ({ row, field, spec, value, detail }) => {
      if (!repo?.update) {
        dispatch(showNotification({ message: "حدث خطأ", type: "error" }));
        // eslint-disable-next-line no-console
        console.error(
          `[inlineEdit] factory key "${config?.api}" has no update()`,
        );
        return { ok: false };
      }

      setSaving(true);
      try {
        // `detail` is already loaded when the field used hydrateOnOpen, so a
        // save costs at most one GET in total, never two.
        let source = detail;
        if (!source && config.hydrate !== false && repo.getById) {
          source = await fetchDetail(row);
          // A successful-but-empty detail must NOT fall back to the list row:
          // that row is the narrower projection hydrate exists to distrust, and
          // a full-replace PUT built from it would blank every field it omits.
          if (!source) {
            const message = "تعذر جلب البيانات، حاول مرة أخرى";
            dispatch(showNotification({ message, type: "error" }));
            return { ok: false, message };
          }
        }
        if (!source) source = row;

        const base = config.toPayload(source, row) ?? {};
        const patch = spec.toPatch
          ? spec.toPatch(value, source, row)
          : { [spec.payloadKey ?? field]: value };

        await repo.update(getId(row), { ...base, ...patch });

        dispatch(
          showNotification({
            message: config.successMessage ?? "تم التعديل بنجاح",
            type: "success",
          }),
        );
        await config.onSaved?.();
        return { ok: true };
      } catch (err) {
        const message = readError(err);
        dispatch(showNotification({ message, type: "error" }));
        return { ok: false, message };
      } finally {
        if (alive.current) setSaving(false);
      }
    },
    [repo, config, dispatch, fetchDetail, getId],
  );

  return { save, saving, fetchDetail };
};
